import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import './css/IngestionReviewView.css';

const apiUrl = API_URL;

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const IngestionReviewView = () => {
  const axios = axiosInstance;
  const [nations, setNations] = useState([]);
  const [selectedNationId, setSelectedNationId] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [staleCommanders, setStaleCommanders] = useState([]);
  const [isLoadingNations, setIsLoadingNations] = useState(false);
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [isUpdatingCommander, setIsUpdatingCommander] = useState(false);
  const [activeRowId, setActiveRowId] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [settings, setSettings] = useState({
    newWindowHours: 6,
    staleWindowHours: 3,
    minScore: 68,
    maxNewCommanderHistoryCount: 0,
    requireEstablishedOld: true,
    requireStrongSignal: true
  });

  const sortedNations = useMemo(() => {
    return [...nations].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
  }, [nations]);

  const fetchNations = useCallback(async () => {
    setIsLoadingNations(true);
    try {
      const response = await axios.get(`${apiUrl}/nations`);
      const nationList = response.data || [];
      setNations(nationList);
      if (nationList.length && !selectedNationId) {
        setSelectedNationId(nationList[0]._id);
      }
    } catch (error) {
      console.error('Error loading nations for ingestion review:', error);
    } finally {
      setIsLoadingNations(false);
    }
  }, [axios, selectedNationId]);

  const runReview = useCallback(async () => {
    if (!selectedNationId) return;
    setIsLoadingReview(true);
    try {
      const response = await axios.get(`${apiUrl}/commanders/ingestion-review`, {
        params: {
          nationId: selectedNationId,
          limit: 60,
          newWindowHours: settings.newWindowHours,
          staleWindowHours: settings.staleWindowHours,
          minScore: settings.minScore,
          maxNewCommanderHistoryCount: settings.maxNewCommanderHistoryCount,
          requireEstablishedOld: settings.requireEstablishedOld,
          requireStrongSignal: settings.requireStrongSignal
        }
      });
      setSuggestions(response.data?.suggestions || []);
      setStaleCommanders(response.data?.staleCommanders || []);
      setMetadata(response.data?.metadata || null);
    } catch (error) {
      console.error('Error loading ingestion review:', error);
      setSuggestions([]);
      setStaleCommanders([]);
      setMetadata(null);
    } finally {
      setIsLoadingReview(false);
    }
  }, [axios, selectedNationId, settings]);

  const handleMerge = async (suggestion) => {
    if (!suggestion?.newerCommander?._id || !suggestion?.olderCommander?._id) return;
    const confirmed = window.confirm(
      `Merge "${suggestion.olderCommander.name}" into "${suggestion.newerCommander.name}"?`
    );
    if (!confirmed) return;

    setIsMerging(true);
    setActiveRowId(suggestion.suggestionKey);
    try {
      await axios.post(`${apiUrl}/commanders/merge`, {
        commanderId1: suggestion.newerCommander._id,
        commanderId2: suggestion.olderCommander._id
      });
      await runReview();
    } catch (error) {
      console.error('Error merging commanders from review:', error);
      window.alert('Failed to merge commanders.');
    } finally {
      setIsMerging(false);
      setActiveRowId('');
    }
  };

  const handleMarkCurrent = async (commander) => {
    if (!commander?._id) return;
    setIsUpdatingCommander(true);
    setActiveRowId(String(commander._id));
    try {
      await axios.patch(`${apiUrl}/commanders/${commander._id}/mark-current`);
      await runReview();
    } catch (error) {
      console.error('Error marking commander current:', error);
      window.alert('Failed to mark commander current.');
    } finally {
      setIsUpdatingCommander(false);
      setActiveRowId('');
    }
  };

  const handleDeleteCommander = async (commander) => {
    if (!commander?._id || !commander?.nation?._id) return;
    const confirmed = window.confirm(
      `Delete "${commander.name}"?\n\nThis will also delete associated city tiles.`
    );
    if (!confirmed) return;

    setIsUpdatingCommander(true);
    setActiveRowId(String(commander._id));
    try {
      await axios.delete(`${apiUrl}/nations/${commander.nation._id}/commanders/${commander._id}`);
      await runReview();
    } catch (error) {
      console.error('Error deleting stale commander:', error);
      window.alert('Failed to delete commander.');
    } finally {
      setIsUpdatingCommander(false);
      setActiveRowId('');
    }
  };

  const handleArchiveCommander = async (commander) => {
    if (!commander?._id) return;
    const confirmed = window.confirm(
      `Archive "${commander.name}" as retired?\n\nThis keeps history/events but removes them from active commander workflows.`
    );
    if (!confirmed) return;

    setIsUpdatingCommander(true);
    setActiveRowId(String(commander._id));
    try {
      await axios.patch(`${apiUrl}/commanders/${commander._id}/archive`, {
        reason: 'Archived from ingestion review'
      });
      await runReview();
    } catch (error) {
      console.error('Error archiving stale commander:', error);
      window.alert('Failed to archive commander.');
    } finally {
      setIsUpdatingCommander(false);
      setActiveRowId('');
    }
  };

  useEffect(() => {
    fetchNations();
  }, [fetchNations]);

  useEffect(() => {
    if (!selectedNationId) return;
    runReview();
  }, [selectedNationId, runReview]);

  return (
    <div className="ingestion-review-view">
      <div className="ingestion-review-header">
        <h2>Review Ingestion</h2>
      </div>

      <p className="ingestion-review-description">
        Review proposed commander merges and resolve stale commanders so ingestion stays current.
      </p>

      <div className="ingestion-review-controls">
        <label htmlFor="irv-nation">Nation</label>
        <select
          id="irv-nation"
          value={selectedNationId}
          onChange={(event) => setSelectedNationId(event.target.value)}
          disabled={isLoadingNations || isLoadingReview}
        >
          {sortedNations.map((nation) => (
            <option key={nation._id} value={nation._id}>
              {nation.number}
            </option>
          ))}
        </select>
      </div>

      <div className="ingestion-review-settings">
        <div className="ingestion-review-setting">
          <label htmlFor="irv-new-window">New Window (hours)</label>
          <input
            id="irv-new-window"
            type="number"
            min="1"
            max="48"
            value={settings.newWindowHours}
            onChange={(event) =>
              setSettings((previous) => ({
                ...previous,
                newWindowHours: Number(event.target.value || 1)
              }))
            }
          />
        </div>
        <div className="ingestion-review-setting">
          <label htmlFor="irv-stale-window">Stale Window (hours)</label>
          <input
            id="irv-stale-window"
            type="number"
            min="1"
            max="72"
            value={settings.staleWindowHours}
            onChange={(event) =>
              setSettings((previous) => ({
                ...previous,
                staleWindowHours: Number(event.target.value || 1)
              }))
            }
          />
        </div>
        <div className="ingestion-review-setting">
          <label htmlFor="irv-min-score">Min Score</label>
          <input
            id="irv-min-score"
            type="number"
            min="45"
            max="95"
            value={settings.minScore}
            onChange={(event) =>
              setSettings((previous) => ({
                ...previous,
                minScore: Number(event.target.value || 45)
              }))
            }
          />
        </div>
        <div className="ingestion-review-setting">
          <label htmlFor="irv-max-history">Max New History Count</label>
          <input
            id="irv-max-history"
            type="number"
            min="0"
            max="30"
            value={settings.maxNewCommanderHistoryCount}
            onChange={(event) =>
              setSettings((previous) => ({
                ...previous,
                maxNewCommanderHistoryCount: Number(event.target.value || 0)
              }))
            }
          />
        </div>
        <label className="ingestion-review-checkbox">
          <input
            type="checkbox"
            checked={settings.requireEstablishedOld}
            onChange={(event) =>
              setSettings((previous) => ({
                ...previous,
                requireEstablishedOld: event.target.checked
              }))
            }
          />
          Require old commander to have history
        </label>
        <label className="ingestion-review-checkbox">
          <input
            type="checkbox"
            checked={settings.requireStrongSignal}
            onChange={(event) =>
              setSettings((previous) => ({
                ...previous,
                requireStrongSignal: event.target.checked
              }))
            }
          />
          Require strong signal gate
        </label>

        <button
          className="ingestion-review-run-button"
          onClick={runReview}
          disabled={!selectedNationId || isLoadingReview || isMerging || isUpdatingCommander}
        >
          {isLoadingReview ? 'Running...' : 'Run Review'}
        </button>
      </div>

      {metadata && (
        <div className="ingestion-review-meta">
          Active: {metadata.activeCommanders ?? 0} | New Candidates: {metadata.newCandidates ?? 0} | Stale Candidates: {metadata.staleCandidates ?? 0}
        </div>
      )}

      <div className="ingestion-review-section">
        <h3>Proposed Merges</h3>
        {suggestions.length === 0 ? (
          <div className="ingestion-review-empty">No merge suggestions for current settings.</div>
        ) : (
          <div className="ingestion-review-list">
            {suggestions.map((suggestion) => (
              <div className="ingestion-review-item" key={suggestion.suggestionKey}>
                <div className="ingestion-review-item-header">
                  <strong>Score: {suggestion.score}</strong>
                  <span className={`ingestion-review-confidence ${suggestion.confidence}`}>
                    {suggestion.confidence}
                  </span>
                </div>

                <div className="ingestion-review-pair">
                  <div className="ingestion-review-side">
                    <h4>Keep (newer)</h4>
                    <p><strong>{suggestion.newerCommander.name}</strong> ({suggestion.newerCommander.alliance?.acronym || 'N/A'})</p>
                    <p>BP: {(suggestion.newerCommander.battlePower || 0).toLocaleString()}</p>
                    <p>City Lv: {suggestion.newerCommander.cityLevel ?? 'N/A'}</p>
                    <p>Updated: {formatDateTime(suggestion.newerCommander.updatedAt)}</p>
                  </div>
                  <div className="ingestion-review-side">
                    <h4>Merge (older)</h4>
                    <p><strong>{suggestion.olderCommander.name}</strong> ({suggestion.olderCommander.alliance?.acronym || 'N/A'})</p>
                    <p>BP: {(suggestion.olderCommander.battlePower || 0).toLocaleString()}</p>
                    <p>City Lv: {suggestion.olderCommander.cityLevel ?? 'N/A'}</p>
                    <p>Updated: {formatDateTime(suggestion.olderCommander.updatedAt)}</p>
                  </div>
                </div>

                <div className="ingestion-review-reasons">
                  {(suggestion.reasons || []).map((reason) => (
                    <span key={reason} className="ingestion-review-chip">
                      {reason}
                    </span>
                  ))}
                </div>

                <div className="ingestion-review-actions">
                  <button
                    onClick={() => handleMerge(suggestion)}
                    disabled={isMerging || isLoadingReview || isUpdatingCommander}
                  >
                    {isMerging && activeRowId === suggestion.suggestionKey ? 'Merging...' : 'Merge Pair'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ingestion-review-section">
        <h3>Stale Commanders (Not New)</h3>
        {staleCommanders.length === 0 ? (
          <div className="ingestion-review-empty">No stale commanders needing review.</div>
        ) : (
          <table className="ingestion-review-stale-table">
            <thead>
              <tr>
                <th>Commander</th>
                <th>Alliance</th>
                <th>BP</th>
                <th>History Count</th>
                <th>Last Updated</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staleCommanders.map((commander) => (
                <tr key={commander._id}>
                  <td>{commander.name}</td>
                  <td>{commander.alliance?.acronym || 'N/A'}</td>
                  <td>{(commander.battlePower || 0).toLocaleString()}</td>
                  <td>{commander.historyCount ?? 0}</td>
                  <td>{formatDateTime(commander.updatedAt)}</td>
                  <td>{String(commander.status || 'active')}</td>
                  <td className="ingestion-review-stale-actions">
                    <button
                      onClick={() => handleMarkCurrent(commander)}
                      disabled={isUpdatingCommander || isLoadingReview || isMerging}
                    >
                      {isUpdatingCommander && activeRowId === String(commander._id) ? 'Updating...' : 'Mark Current'}
                    </button>
                    <button
                      onClick={() => handleArchiveCommander(commander)}
                      disabled={
                        isUpdatingCommander
                        || isLoadingReview
                        || isMerging
                        || String(commander?.status || 'active').toLowerCase() === 'retired'
                      }
                    >
                      {String(commander?.status || 'active').toLowerCase() === 'retired' ? 'Archived' : 'Archive'}
                    </button>
                    <button
                      className="danger"
                      onClick={() => handleDeleteCommander(commander)}
                      disabled={isUpdatingCommander || isLoadingReview || isMerging}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default IngestionReviewView;
